import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CalendarDays, CheckCircle2 } from 'lucide-react'
import { PeriodSelector } from '@/components/analytics'
import { Badge, Button, Card } from '@/components/ui'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { formatNumber, type MetricPeriod } from '@/lib/utils'
import {
  getPedagogyReviewDispatchOverview,
  listPedagogyReviewEvaluators,
  setPedagogyReviewWorkloadPlan,
  type PedagogyReviewDispatchOverviewRow,
  type PedagogyReviewEvaluator,
} from '@/services/pedagogyReviewQueueService'

type AvailabilityStatus = PedagogyReviewEvaluator['availabilityStatus']

type WeekRow = {
  periodId: number
  weekNumberInPeriod: number
  weekStart: string
  weekEnd: string
}

type WeekPlanDraft = {
  targetCount: string
  dispatchPaused: boolean
}

type GroupedEvaluator = {
  evaluatorUserId: string
  evaluatorName: string | null
  tgUsername: string | null
  availabilityStatus: AvailabilityStatus
  availabilityNote: string | null
  isCurrentSpecialist: boolean
  weeks: Record<number, PedagogyReviewDispatchOverviewRow>
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}.${month}.${year}`
}

function formatDateIso(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

function initials(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean)
  return (parts.slice(0, 2).map(part => part[0]).join('') || '?').toUpperCase()
}

function todayIso(): string {
  return formatDateIso(new Date())
}

function parseCount(value: string): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0
}

function toPlanDraft(row: PedagogyReviewDispatchOverviewRow): WeekPlanDraft {
  return {
    targetCount: String(row.targetCount),
    dispatchPaused: row.dispatchPaused,
  }
}

function isWeekEditable(week: WeekRow, period: MetricPeriod | null): boolean {
  if (!period) return false

  const today = todayIso()
  if (today > week.weekEnd) return false

  const start = new Date(`${period.period_start}T00:00:00`)
  if (Number.isNaN(start.getTime())) return false
  start.setDate(start.getDate() - 3)

  return today >= formatDateIso(start)
}

function isCurrentWeek(week: WeekRow): boolean {
  const today = todayIso()
  return today >= week.weekStart && today <= week.weekEnd
}

export function PedagogyReviewDispatchPage() {
  const navigate = useNavigate()
  const [periods, setPeriods] = useState<MetricPeriod[]>([])
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null)
  const [weeks, setWeeks] = useState<WeekRow[]>([])
  const [overview, setOverview] = useState<PedagogyReviewDispatchOverviewRow[]>([])
  const [evaluators, setEvaluators] = useState<PedagogyReviewEvaluator[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [planTarget, setPlanTarget] = useState<PedagogyReviewDispatchOverviewRow | null>(null)
  const [planDraft, setPlanDraft] = useState<WeekPlanDraft | null>(null)

  const selectedPeriod = useMemo(
    () => periods.find(period => period.period_id === selectedPeriodId) ?? null,
    [periods, selectedPeriodId]
  )

  const selectedPeriodIndex = useMemo(
    () => periods.findIndex(period => period.period_id === selectedPeriodId),
    [periods, selectedPeriodId]
  )

  const loadPeriods = useCallback(async () => {
    const supabase = getSupabase()
    const { data, error: periodsError } = await withAuthRecovery(() =>
      supabase.from('metric_periods').select('*').order('period_start', { ascending: false })
    )

    if (periodsError) throw periodsError

    const gateDate = new Date()
    gateDate.setDate(gateDate.getDate() + 3)
    const openUntil = formatDateIso(gateDate)
    const today = todayIso()
    const nextPeriods = ((data ?? []) as MetricPeriod[])
      .filter(period => period.period_start <= openUntil)

    setPeriods(nextPeriods)
    setSelectedPeriodId(current => {
      if (current != null && nextPeriods.some(period => period.period_id === current)) return current
      const fallback = nextPeriods.find(period => period.period_start <= today && today <= period.period_end) ?? nextPeriods[0] ?? null
      return fallback?.period_id ?? null
    })
  }, [])

  const loadPeriodData = useCallback(async (periodId: number) => {
    const supabase = getSupabase()
    const { data: weeksData, error: weeksError } = await withAuthRecovery(() =>
      supabase
        .from('metric_weeks')
        .select('period_id, week_number_in_period, week_start, week_end')
        .eq('period_id', periodId)
        .order('week_number_in_period', { ascending: true })
    )

    if (weeksError) throw weeksError

    const nextWeeks = ((weeksData ?? []) as Array<Record<string, unknown>>).map(row => ({
      periodId: Number(row.period_id),
      weekNumberInPeriod: Number(row.week_number_in_period),
      weekStart: String(row.week_start),
      weekEnd: String(row.week_end),
    }))
    setWeeks(nextWeeks)

    const [nextEvaluators, weekSlices] = await Promise.all([
      listPedagogyReviewEvaluators(),
      Promise.all(nextWeeks.map(week => getPedagogyReviewDispatchOverview(periodId, week.weekNumberInPeriod))),
    ])

    setEvaluators(nextEvaluators)
    setOverview(weekSlices.flat())
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await loadPeriods()
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setLoading(false)
    }
  }, [loadPeriods])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (selectedPeriodId == null) return

    let cancelled = false

    async function run() {
      setLoading(true)
      setError(null)
      try {
        await loadPeriodData(selectedPeriodId)
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : String(loadError))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [loadPeriodData, selectedPeriodId])

  const groupedEvaluators = useMemo<GroupedEvaluator[]>(() => {
    const map = new Map<string, GroupedEvaluator>()

    for (const evaluator of evaluators) {
      map.set(evaluator.evaluatorUserId, {
        evaluatorUserId: evaluator.evaluatorUserId,
        evaluatorName: evaluator.evaluatorName,
        tgUsername: evaluator.tgUsername,
        availabilityStatus: evaluator.availabilityStatus,
        availabilityNote: evaluator.removalReason,
        isCurrentSpecialist: true,
        weeks: {},
      })
    }

    for (const row of overview) {
      const current = map.get(row.evaluatorUserId)
      if (current) {
        current.weeks[row.weekNumberInPeriod] = row
        continue
      }

      map.set(row.evaluatorUserId, {
        evaluatorUserId: row.evaluatorUserId,
        evaluatorName: row.evaluatorName,
        tgUsername: null,
        availabilityStatus: row.availabilityStatus,
        availabilityNote: row.availabilityNote,
        isCurrentSpecialist: false,
        weeks: { [row.weekNumberInPeriod]: row },
      })
    }

    return Array.from(map.values()).sort((left, right) => {
      const leftName = left.evaluatorName ?? left.evaluatorUserId
      const rightName = right.evaluatorName ?? right.evaluatorUserId
      return leftName.localeCompare(rightName, 'ru')
    })
  }, [evaluators, overview])

  const totals = useMemo(() => {
    return overview.reduce(
      (acc, row) => {
        acc.plan += row.targetCount
        acc.done += row.doneCount
        acc.open += row.openCount
        acc.poolSize = Math.max(acc.poolSize, row.poolSize)
        return acc
      },
      { plan: 0, done: 0, open: 0, poolSize: 0 }
    )
  }, [overview])

  const handleSavePlan = useCallback(async () => {
    if (!planTarget || !planDraft) return

    setBusy(true)
    setError(null)
    try {
      await setPedagogyReviewWorkloadPlan({
        evaluatorUserId: planTarget.evaluatorUserId,
        periodId: planTarget.periodId,
        weekNumberInPeriod: planTarget.weekNumberInPeriod,
        targetCount: parseCount(planDraft.targetCount),
        bufferCount: 0,
        maxOpenCount: 1,
        stretchLimit: 0,
        allowSelfRequest: true,
        dispatchPaused: planDraft.dispatchPaused,
      })
      setNotice('Недельный план обновлён')
      setPlanTarget(null)
      setPlanDraft(null)
      if (selectedPeriodId != null) {
        await loadPeriodData(selectedPeriodId)
      }
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError))
    } finally {
      setBusy(false)
    }
  }, [loadPeriodData, planDraft, planTarget, selectedPeriodId])

  if (loading && periods.length === 0 && overview.length === 0 && evaluators.length === 0) {
    return (
      <div className="space-y-4 pb-24">
        <Card>
          <div className="py-6 text-center text-sm text-muted-foreground">Загружаем планирование нагрузки…</div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-3 pb-24">
      <Button type="button" size="sm" variant="outline" className="inline-flex items-center justify-center gap-1.5" onClick={() => navigate(pedagogyPaths.department)}>
        <ArrowLeft className="h-4 w-4" />
        Назад
      </Button>
      <div className="rounded-xl border border-border bg-card px-2 shadow-sm">
        <PeriodSelector
          currentPeriod={selectedPeriod}
          onPrevPeriod={() => {
            if (selectedPeriodIndex < 0 || selectedPeriodIndex >= periods.length - 1) return
            setSelectedPeriodId(periods[selectedPeriodIndex + 1]?.period_id ?? null)
          }}
          onNextPeriod={() => {
            if (selectedPeriodIndex <= 0) return
            setSelectedPeriodId(periods[selectedPeriodIndex - 1]?.period_id ?? null)
          }}
          canGoPrev={selectedPeriodIndex >= 0 && selectedPeriodIndex < periods.length - 1}
          canGoNext={selectedPeriodIndex > 0}
          showViewModeSelector={false}
          isCurrentPeriod
          compact
        />
      </div>

      {error && (
        <Card className="border border-error/40">
          <div className="flex items-start gap-2 text-sm text-error">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {notice && (
        <Card>
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            <span>{notice}</span>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2 px-1 text-xs text-muted-foreground">
        <span className="rounded-full border border-border bg-card px-3 py-1">План <strong className="text-foreground">{formatNumber(totals.plan)}</strong></span>
        <span className="rounded-full border border-border bg-card px-3 py-1">Выполнено <strong className="text-foreground">{formatNumber(totals.done)}</strong></span>
        <span className="rounded-full border border-border bg-card px-3 py-1">Открыто <strong className="text-foreground">{formatNumber(totals.open)}</strong></span>
        <span className="rounded-full border border-border bg-card px-3 py-1">Пул <strong className="text-foreground">{formatNumber(totals.poolSize)}</strong></span>
      </div>

      <div>
        {loading && groupedEvaluators.length === 0 && (
          <Card className="border border-border shadow-sm">
            <div className="px-1 py-8 text-center text-sm text-muted-foreground">
              Загружаем планирование…
            </div>
          </Card>
        )}

        {!loading && groupedEvaluators.length === 0 && (
          <Card className="border border-border shadow-sm">
            <div className="px-1 py-8 text-center text-sm text-muted-foreground">
              В разделе пока нет специалистов.
            </div>
          </Card>
        )}

        <Card className="overflow-hidden border border-border border-t-2 border-t-primary/70 p-0 shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-[1120px] w-full border-collapse text-sm">
              <thead className="border-b border-border bg-muted/20">
                <tr className="text-left text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  <th className="w-[290px] px-3 py-2 font-medium">Сотрудник</th>
                  {weeks.map(week => (
                    <th key={week.weekNumberInPeriod} className={`w-[158px] px-2 py-2 font-medium ${isCurrentWeek(week) ? 'bg-primary/5 text-primary' : ''}`}>
                      <div>Н{week.weekNumberInPeriod}</div>
                      <div className="mt-0.5 normal-case tracking-normal text-[10px]">
                        {formatDate(week.weekStart)} - {formatDate(week.weekEnd)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedEvaluators.map((group, index) => (
                  <tr key={group.evaluatorUserId} className="align-middle">
                    <td className={`${index > 0 ? 'border-t border-border' : ''} px-3 py-2`}>
                      <div className="flex min-h-12 items-center justify-between gap-3 rounded-lg bg-gradient-to-r from-primary/[0.06] to-muted/20 px-2.5 py-1.5">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                              {initials(group.evaluatorName ?? group.evaluatorUserId)}
                            </span>
                            <div className="min-w-0">
                              <div className="truncate text-[13px] font-semibold leading-4 text-foreground">
                                {group.evaluatorName ?? group.evaluatorUserId}
                              </div>
                              <div className="truncate text-[10px] leading-3 text-muted-foreground">
                                {group.tgUsername ? `@${group.tgUsername}` : group.isCurrentSpecialist ? 'Действующий специалист' : 'История'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    {weeks.map(week => {
                      const row = group.weeks[week.weekNumberInPeriod]
                      const canEdit = isWeekEditable(week, selectedPeriod) && row != null

                      if (!row) {
                        return (
                          <td key={week.weekNumberInPeriod} className={`${index > 0 ? 'border-t border-border' : ''} px-2 py-2`}>
                            <div className="flex min-h-12 items-center rounded-lg bg-muted/10 px-2 text-[10px] text-muted-foreground">
                              нет данных
                            </div>
                          </td>
                        )
                      }

                      return (
                        <td key={week.weekNumberInPeriod} className={`${index > 0 ? 'border-t border-border' : ''} px-2 py-2`}>
                          <div className={`grid min-h-12 grid-cols-[auto_1fr_auto] items-center gap-2 rounded-lg px-2 ${isCurrentWeek(week) ? 'bg-primary/[0.08] ring-1 ring-inset ring-primary/20' : 'bg-muted/10'}`}>
                            <div className="text-xl font-semibold leading-none tabular-nums text-foreground">
                                {formatNumber(row.targetCount)}
                            </div>
                            <div className="min-w-0 text-[10px] leading-4 text-muted-foreground">
                              <div>Факт {formatNumber(row.doneCount)}</div>
                              <div>Открыто {formatNumber(row.openCount)}</div>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              {row.dispatchPaused && (
                                <Badge variant="warning" className="px-1.5 py-0.5 text-[10px]">
                                  пауза
                                </Badge>
                              )}
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                className="inline-flex h-7 items-center justify-center px-2 text-[10px] leading-none"
                                disabled={!canEdit}
                                title={canEdit ? 'Изменить план' : 'Неделя закрыта для изменений'}
                                onClick={() => {
                                  setPlanTarget(row)
                                  setPlanDraft(toPlanDraft(row))
                                }}
                              >
                                Изм.
                              </Button>
                            </div>
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <BottomSheet
        isOpen={planTarget != null}
        onClose={() => {
          setPlanTarget(null)
          setPlanDraft(null)
        }}
        title="Недельный план"
        className="border border-border shadow-2xl sm:max-w-xl"
      >
        {planTarget && planDraft && (
          <div className="space-y-4 bg-gradient-to-b from-primary/[0.04] to-background p-4 pb-5 sm:p-5">
            <div className="rounded-xl border border-primary/15 bg-primary/[0.06] p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {initials(planTarget.evaluatorName ?? planTarget.evaluatorUserId)}
                </div>
                <div>
                  <div className="font-semibold text-foreground">
                {planTarget.evaluatorName ?? planTarget.evaluatorUserId}
                  </div>
                  <div className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    Неделя {planTarget.weekNumberInPeriod}: {formatDate(planTarget.weekStart)} - {formatDate(planTarget.weekEnd)}
                  </div>
                </div>
              </div>
            </div>

            <label className="block space-y-2">
              <span className="text-sm font-medium text-foreground">План на неделю</span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className="w-full rounded-xl border-2 border-primary/20 bg-background px-4 py-3 text-2xl font-semibold tabular-nums text-foreground outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                value={planDraft.targetCount}
                onChange={event =>
                  setPlanDraft(draft => (draft ? { ...draft, targetCount: event.target.value } : draft))
                }
              />
            </label>

            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-background px-3 py-3 text-sm text-foreground transition hover:border-primary/40">
              <input
                className="h-4 w-4 accent-primary"
                type="checkbox"
                checked={planDraft.dispatchPaused}
                onChange={event =>
                  setPlanDraft(draft => (draft ? { ...draft, dispatchPaused: event.target.checked } : draft))
                }
              />
              Остановить выдачу на эту неделю
            </label>

            <Button type="button" size="sm" className="inline-flex w-full items-center justify-center leading-none" disabled={busy} onClick={handleSavePlan}>
              Сохранить план
            </Button>
          </div>
        )}
      </BottomSheet>

    </div>
  )
}
